import type { ServerSideSelectionState } from '@civilgrid/ui/components';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import type { ProjectsSearchPayload } from '~/api/projectsV3';
import type { Project } from '~/common/types';
import { useBulkInviteMembers } from '../../../queries';
import { setTableSelection } from '../../../store';

export type InviteDialogBulkProps = {
  projectId?: string;
  projectIds?: string[];
  projectMemberUserIds?: string[];
  onSave?: () => void | Promise<void>;
  onBulkInvite?: (payload: { userIds: string[]; memberData?: { email: string; role: string }[] }) => Promise<void>;
};

export type UseBulkInviteParams = {
  organizationId: string | null;
  buildSearchPayloadForBulk: () => ProjectsSearchPayload;
  getProjectById?: (id: string) => Project | undefined;
};

export function useBulkInvite({ organizationId, buildSearchPayloadForBulk, getProjectById }: UseBulkInviteParams) {
  const [pendingSelection, setPendingSelection] = useState<ServerSideSelectionState | null>(null);
  const queryClient = useQueryClient();
  const mutation = useBulkInviteMembers();

  const open = useCallback((selection: ServerSideSelectionState) => {
    setPendingSelection(selection);
  }, []);

  const close = useCallback(() => {
    setPendingSelection(null);
  }, []);

  const inviteDialogProps = useMemo((): InviteDialogBulkProps | null => {
    if (!pendingSelection || !organizationId) return null;

    const isSingle = pendingSelection.mode === 'partial' && pendingSelection.selectedIds.length === 1;

    if (isSingle && getProjectById) {
      const projectId = pendingSelection.selectedIds[0];
      const project = getProjectById(projectId);
      const projectMemberUserIds = project?.members?.map((m) => String(m.user?.id ?? '')).filter(Boolean) ?? [];
      return {
        projectId,
        projectMemberUserIds,
        onSave: () => {
          void queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
      };
    }

    const onBulkInvite = async (payload: { userIds: string[]; memberData?: { email: string; role: string }[] }) => {
      const selection =
        pendingSelection.mode === 'all'
          ? { ...pendingSelection, search: buildSearchPayloadForBulk() }
          : pendingSelection;
      await mutation.mutateAsync({
        organizationId,
        selection,
        userIds: payload.userIds ?? [],
        memberData: payload.memberData,
      });
      setPendingSelection(null);
      setTableSelection({ mode: 'partial', selectedIds: [] });
    };

    return {
      onBulkInvite,
    };
  }, [pendingSelection, organizationId, getProjectById, buildSearchPayloadForBulk, mutation, queryClient]);

  return {
    isOpen: pendingSelection !== null,
    open,
    close,
    inviteDialogProps,
    isPending: mutation.isPending,
  };
}
